import { memo, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../hooks/useAuth";
import { useRealtimeMetrics } from "../hooks/useRealtimeData";

// Import chart components directly from recharts
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";


const MenuIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const QuantumIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const DimensionIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

// Visual update indicator component
const UpdateIndicator = ({ isUpdating }: { isUpdating: boolean }) => (
  <div className={`flex items-center gap-1 ${isUpdating ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
    <span className="text-xs text-green-600">Updating</span>
  </div>
);

// Metric card with update animation (memoized to prevent unnecessary re-renders)
const MetricCard = memo(({
  title,
  value,
  unit,
  icon,
  isUpdating,
  color = "red"
}: {
  title: string;
  value: string | number;
  unit?: string;
  icon: React.ReactNode;
  isUpdating: boolean;
  color?: string;
}) => (
  <div className={`bg-white border border-${color}-200 rounded-xl p-4 shadow-md transition-all duration-300 ${
    isUpdating ? 'ring-2 ring-green-400 ring-opacity-50 bg-green-50' : 'hover:shadow-lg'
  }`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-gray-600 text-xs font-medium uppercase tracking-wide">{title}</span>
      <span className={`text-${color}-600`}>{icon}</span>
    </div>
    <div className="flex items-center gap-2">
      <div className={`text-2xl font-bold text-gray-900 transition-all duration-300 ${isUpdating ? 'scale-105' : ''}`}>
        {value}
        {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
      </div>
      {isUpdating && <UpdateIndicator isUpdating={isUpdating} />}
    </div>
  </div>
));

interface DashboardMetrics {
  backendLatency: Record<string, number>;
  requestStats: {
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    errorRate: number;
    avgResponseTime: number;
  };
  transactionMetrics: {
    totalVolume: number;
    completedTransactions: number;
    pendingTransactions: number;
    failedTransactions: number;
  };
}

interface AIProcess {
  id: string;
  name: string;
  cpu: number;
  memory: number;
  disk: number;
  network: number;
  status: "running" | "sleeping" | "stopped" | "completed";
  pid: string;
  user: string;
  progress: number;
  duration: number;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Real-time metrics with update indicators
  const { isUpdating, lastUpdate, refresh } = useRealtimeMetrics();
  const [updatingFields, setUpdatingFields] = useState<Set<string>>(new Set());
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'error'>('connected');

  // Helper to trigger visual update indicator for a specific field
  const triggerUpdateIndicator = useCallback((fieldName: string) => {
    setUpdatingFields(prev => new Set([...prev, fieldName]));
    setTimeout(() => {
      setUpdatingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldName);
        return newSet;
      });
    }, 500);
  }, []);

  // Check if user can customize dashboard (only superadmin and admin)
  const canCustomizeDashboard = useMemo(() =>
    ['superadmin', 'admin'].includes(user?.role || ''),
    [user?.role]
  );
  const isMasterAdmin = useMemo(() => user?.role === 'superadmin', [user?.role]);

  // Drag and drop state for arrangeable sections
  const [dashboardSections, setDashboardSections] = useState([
    // Business Metrics (always visible for business users)
    { id: 'latency', title: 'Backend Latency', order: 0, visible: true, category: 'business' },
    { id: 'charts', title: 'Performance Charts', order: 1, visible: true, category: 'business' },
    { id: 'map', title: 'การติดตามตำแหน่ง (Location Tracking)', order: 2, visible: true, category: 'business' },
    // AI/Agent Systems (for internal use, can be moved to /pages later)
    { id: 'resources', title: 'Resource Monitoring', order: 3, visible: isMasterAdmin, category: 'agentic' },
    { id: 'processes', title: 'AI Processes', order: 4, visible: isMasterAdmin, category: 'agentic' },
    // Admin-specific sections (only for master admin)
    { id: 'quick-actions', title: 'Admin Quick Actions', order: 5, visible: isMasterAdmin, category: 'admin' },
    { id: 'system-health', title: 'System Health', order: 6, visible: isMasterAdmin, category: 'admin' },
    { id: 'security-alerts', title: 'Security Alerts', order: 7, visible: isMasterAdmin, category: 'admin' },
    { id: 'deployment-status', title: 'Deployment Status', order: 8, visible: isMasterAdmin, category: 'admin' },
  ]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [processes, setProcesses] = useState<AIProcess[]>([]);
  const [resourceHistory, setResourceHistory] = useState<{
    cpu: number[];
    memory: number[];
    disk: number[];
    network: number[];
  }>({ cpu: [], memory: [], disk: [], network: [] });

  // Drag and drop handlers (optimized with useCallback)
  const handleDragStart = useCallback((_sectionId: string) => {
    // Drag start logic here
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((_targetSectionId: string) => {
    // Drop logic here
  }, []);

  const toggleSectionVisibility = useCallback((sectionId: string) => {
    setDashboardSections(prev =>
      prev.map(section =>
        section.id === sectionId
          ? { ...section, visible: !section.visible }
          : section
      )
    );
  }, []);

  const resetLayout = useCallback(() => {
    setDashboardSections([
      // Business Metrics (always visible for business users)
      { id: 'latency', title: 'Backend Latency', order: 0, visible: true, category: 'business' },
      { id: 'charts', title: 'Performance Charts', order: 1, visible: true, category: 'business' },
      { id: 'map', title: 'การติดตามตำแหน่ง (Location Tracking)', order: 2, visible: true, category: 'business' },
      // AI/Agent Systems (for internal use, can be moved to /pages later)
      { id: 'resources', title: 'Resource Monitoring', order: 3, visible: isMasterAdmin, category: 'agentic' },
      { id: 'processes', title: 'AI Processes', order: 4, visible: isMasterAdmin, category: 'agentic' },
      // Admin-specific sections (only for master admin)
      { id: 'quick-actions', title: 'Admin Quick Actions', order: 5, visible: isMasterAdmin, category: 'admin' },
      { id: 'system-health', title: 'System Health', order: 6, visible: isMasterAdmin, category: 'admin' },
      { id: 'security-alerts', title: 'Security Alerts', order: 7, visible: isMasterAdmin, category: 'admin' },
      { id: 'deployment-status', title: 'Deployment Status', order: 8, visible: isMasterAdmin, category: 'admin' },
    ]);
  }, [isMasterAdmin]);

  // Memoize visible sections to prevent recalculation
  const visibleSections = useMemo(() =>
    dashboardSections
      .filter(section => section.visible)
      .sort((a, b) => a.order - b.order),
    [dashboardSections]
  );

  // Static chart data (moved outside component to prevent recreation on every render)
const REVENUE_DATA = [
  { name: "Jan", revenue: 40000 },
  { name: "Feb", revenue: 30000 },
  { name: "Mar", revenue: 20000 },
  { name: "Apr", revenue: 27800 },
  { name: "May", revenue: 18900 },
  { name: "Jun", revenue: 23900 },
  { name: "Jul", revenue: 34900 },
];

// const TRANSACTION_DATA = [
//   { name: "Completed", value: 400, color: "#1e3a8a" },
//   { name: "Pending", value: 300, color: "#0891b2" },
//   { name: "Failed", value: 200, color: "#06b6d4" },
// ];

// const LATENCY_DATA = [
//   { name: "Payment", latency: 85 },
//   { name: "Ecommerce", latency: 120 },
//   { name: "Inventory", latency: 45 },
//   { name: "Tenants", latency: 78 },
//   { name: "Analytics", latency: 92 },
//   { name: "Reports", latency: 65 },
// ];

  // Fetch metrics on load with real-time updates
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setConnectionStatus('connected');
        const response = await api.getDashboardMetrics();
        const data = (response as any).data || response;

        console.log('Dashboard API Response:', response);
        console.log('Dashboard Data:', data);

        setMetrics(data);
        triggerUpdateIndicator('metrics');

        // Calculate health score (simplified)
        if (data?.requestStats) {
          // Health score calculation commented out
          // setHealthScore(Math.round((errorScore + latencyScore) / 2));
          // triggerUpdateIndicator('healthScore');
        } else {
          console.error('requestStats is undefined in data:', data);
          // setHealthScore(0);
        }
      } catch (err) {
        console.error('Error fetching metrics:', err);
        setConnectionStatus('error');
        setError(
          err instanceof Error ? err.message : "Failed to fetch metrics",
        );
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchMetrics();

    // Real-time refresh every 2 seconds for critical metrics
    const interval = setInterval(fetchMetrics, 2000);

    // Connection health check every 10 seconds
    const healthCheck = setInterval(() => {
      if (connectionStatus === 'error') {
        setConnectionStatus('disconnected');
        fetchMetrics();
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearInterval(healthCheck);
    };
  }, [connectionStatus, triggerUpdateIndicator]);

  // Fetch revolutionary metrics for Activity Monitor features
  useEffect(() => {
    const fetchRevolutionaryMetrics = async () => {
      try {
        const response = await api.getRevolutionaryMetrics();
        const data = (response as any).data || response;

        // Generate AI processes from metrics
        const aiProcesses: AIProcess[] = [
          {
            id: "predictive-orchestrator",
            name: "Predictive Orchestrator",
            cpu: data?.predictive?.averageAccuracy * 100 || 0,
            memory: data?.predictive?.quantumStates / 10 || 0,
            disk: 0,
            network: data?.predictive?.totalPreloads / 10 || 0,
            status: "running",
            pid: "1001",
            user: "system",
            progress: 75,
            duration: 120
          },
          {
            id: "autonomous-evolution",
            name: "Autonomous Evolution",
            cpu: data?.evolution?.averageFitness * 100 || 0,
            memory: data?.evolution?.totalAgents * 5 || 0,
            disk: data?.evolution?.totalMutations / 10 || 0,
            network: 0,
            status: "running",
            pid: "1002",
            user: "system",
            progress: 60,
            duration: 180
          },
          {
            id: "quantum-swarm",
            name: "Quantum Swarm",
            cpu: data?.quantum?.coherenceLevel * 100 || 0,
            memory: data?.quantum?.totalAgents * 10 || 0,
            disk: 0,
            network: data?.quantum?.entanglementDegree * 100 || 0,
            status: "running",
            pid: "1003",
            user: "system",
            progress: 85,
            duration: 90
          },
          {
            id: "temporal-cache",
            name: "Temporal Cache",
            cpu: 15,
            memory: data?.temporal?.totalStates / 20 || 0,
            disk: data?.temporal?.totalStates / 5 || 0,
            network: 0,
            status: "running",
            pid: "1004",
            user: "system",
            progress: 40,
            duration: 60
          },
          {
            id: "zero-latency-preexec",
            name: "Zero-Latency Pre-Execution",
            cpu: data?.preExecution?.predictionAccuracy * 100 || 0,
            memory: data?.preExecution?.totalPreExecutions / 5 || 0,
            disk: data?.preExecution?.cacheHitRate * 100 || 0,
            network: 0,
            status: "running",
            pid: "1005",
            user: "system",
            progress: 55,
            duration: 150
          }
        ];

        setProcesses(aiProcesses);

        // Update resource history for graphs
        const avgCpu = aiProcesses.reduce((sum, p) => sum + p.cpu, 0) / aiProcesses.length;
        const avgMemory = aiProcesses.reduce((sum, p) => sum + p.memory, 0) / aiProcesses.length;
        const avgDisk = aiProcesses.reduce((sum, p) => sum + p.disk, 0) / aiProcesses.length;
        const avgNetwork = aiProcesses.reduce((sum, p) => sum + p.network, 0) / aiProcesses.length;

        setResourceHistory(prev => ({
          cpu: [...prev.cpu.slice(-29), avgCpu],
          memory: [...prev.memory.slice(-29), avgMemory],
          disk: [...prev.disk.slice(-29), avgDisk],
          network: [...prev.network.slice(-29), avgNetwork]
        }));
      } catch (error) {
        console.error('Error fetching revolutionary metrics:', error);
      }
    };

    fetchRevolutionaryMetrics();
    const interval = setInterval(fetchRevolutionaryMetrics, 2000);
    return () => clearInterval(interval);
  }, []);

  // Sort processes
  // const sortedProcesses = [...processes].sort((a, b) => {
  //   const modifier = sortOrder === "asc" ? 1 : -1;
  //   return (a[sortBy] - b[sortBy]) * modifier;
  // });

  // Update clock
  // useEffect(() => {
  //   const timer = setInterval(() => setCurrentTime(new Date()), 1000);
  //   return () => clearInterval(timer);
  // }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary mt-4"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white overflow-hidden">
      <Sidebar isMobileOpen={isMobileMenuOpen} setIsMobileOpen={setIsMobileMenuOpen} />
      <div className="flex-1 ml-56 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
              {isEditMode && (
                <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">
                  Edit Mode
                </span>
              )}
              {/* Connection Status Indicator */}
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  connectionStatus === 'connected' ? 'bg-green-500' :
                  connectionStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500'
                }`} />
                <span className="text-xs text-gray-500">
                  {connectionStatus === 'connected' ? 'Live' :
                   connectionStatus === 'error' ? 'Disconnected' : 'Reconnecting...'}
                </span>
              </div>
              {/* Last Update Indicator */}
              {lastUpdate && (
                <span className="text-xs text-gray-400">
                  Last update: {lastUpdate.toLocaleTimeString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Manual Refresh Button */}
              <button
                onClick={refresh}
                disabled={isUpdating}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isUpdating
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {isUpdating ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    Updating...
                  </span>
                ) : (
                  'Refresh'
                )}
              </button>
              {canCustomizeDashboard && (
                <>
                  {isEditMode ? (
                    <>
                      <button
                        onClick={() => setIsEditMode(false)}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Done
                      </button>
                      <button
                        onClick={resetLayout}
                        className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Reset Layout
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditMode(true)}
                      className="px-4 py-2 bg-gradient-to-r from-red-600 via-red-500 to-red-600 hover:from-red-700 hover:via-red-600 hover:to-red-700 text-white text-sm font-medium rounded-lg transform transition-all duration-300 hover:scale-[1.03]"
                    >
                      System Monitor
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <MenuIcon />
          </button>
        </header>

        {/* Main Dashboard Content */}
        <main className="flex-1 p-4 overflow-auto">
          {isEditMode && canCustomizeDashboard && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
              <h3 className="text-sm font-semibold text-red-900 mb-2">Customize Your Dashboard</h3>
              <div className="space-y-3">
                {/* Business Metrics Section */}
                <div>
                  <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Business Metrics</h4>
                  <div className="space-y-1">
                    {dashboardSections.filter(s => s.category === 'business').map(section => (
                      <div key={section.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-red-100">
                        <div className="flex items-center gap-3">
                          <div className="cursor-move p-1">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-700">{section.title}</span>
                        </div>
                        <button
                          onClick={() => toggleSectionVisibility(section.id)}
                          className={`px-3 py-1 text-xs font-medium rounded ${
                            section.visible
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {section.visible ? 'Visible' : 'Hidden'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI/Agent Systems Section */}
                <div>
                  <h4 className="text-[10px] font-semibold text-red-500 uppercase tracking-wide mb-1">AI/Agent Systems <span className="text-gray-400 font-normal">(Internal Use - can be moved to /pages later)</span></h4>
                  <div className="space-y-1">
                    {dashboardSections.filter(s => s.category === 'agentic').map(section => (
                      <div key={section.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-red-100">
                        <div className="flex items-center gap-3">
                          <div className="cursor-move p-1">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-700">{section.title}</span>
                        </div>
                        <button
                          onClick={() => toggleSectionVisibility(section.id)}
                          className={`px-3 py-1 text-xs font-medium rounded ${
                            section.visible
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {section.visible ? 'Visible' : 'Hidden'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Admin-specific Section */}
                {isMasterAdmin && (
                  <div>
                    <h4 className="text-[10px] font-semibold text-red-500 uppercase tracking-wide mb-1">Admin Tools <span className="text-gray-400 font-normal">(Master Admin Only)</span></h4>
                    <div className="space-y-1">
                      {dashboardSections.filter(s => s.category === 'admin').map(section => (
                        <div key={section.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-red-100">
                          <div className="flex items-center gap-3">
                            <div className="cursor-move p-1">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                              </svg>
                            </div>
                            <span className="text-sm font-medium text-gray-700">{section.title}</span>
                          </div>
                          <button
                            onClick={() => toggleSectionVisibility(section.id)}
                            className={`px-3 py-1 text-xs font-medium rounded ${
                              section.visible
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {section.visible ? 'Visible' : 'Hidden'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Arrangeable Dashboard Sections */}
          <div className="space-y-6">
            {visibleSections.map((section, index) => {
              const prevSection = visibleSections[index - 1];
              const showCategoryHeader = index === 0 || (prevSection && prevSection.category !== section.category);

                return (
                  <div key={section.id}>
                    {showCategoryHeader && (
                      <div className="mb-3">
                        <h2 className="text-base font-semibold text-gray-900">
                          {section.category === 'business' ? 'Business Metrics' :
                           section.category === 'agentic' ? 'AI/Agent Systems' : 'Admin Tools'}
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                          {section.category === 'business'
                            ? 'Key performance indicators for business operations'
                            : section.category === 'agentic'
                            ? 'Internal AI systems monitoring (can be moved to /pages later)'
                            : 'Administrative tools and system controls (Master Admin only)'}
                        </p>
                      </div>
                    )}
                    <div
                      draggable={isEditMode}
                      onDragStart={() => handleDragStart(section.id)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(section.id)}
                      className={`${
                        isEditMode
                          ? 'border-2 border-dashed border-red-300 bg-red-50/50'
                          : ''
                      } rounded-xl transition-all`}
                    >
                  {section.id === 'resources' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <MetricCard
                          title="Quantum Processing"
                          value={resourceHistory.cpu[resourceHistory.cpu.length - 1]?.toFixed(1) || "0"}
                          unit="%"
                          icon={<QuantumIcon />}
                          isUpdating={updatingFields.has('cpu') || isUpdating}
                          color="red"
                        />
                        <MetricCard
                          title="Context Storage"
                          value={resourceHistory.memory[resourceHistory.memory.length - 1]?.toFixed(1) || "0"}
                          unit="%"
                          icon={<DimensionIcon />}
                          isUpdating={updatingFields.has('memory') || isUpdating}
                          color="blue"
                        />
                        <MetricCard
                          title="Temporal Cache"
                          value={resourceHistory.disk[resourceHistory.disk.length - 1]?.toFixed(1) || "0"}
                          unit="%"
                          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
</svg>}
                          isUpdating={updatingFields.has('disk') || isUpdating}
                          color="purple"
                        />
                        <MetricCard
                          title="Agent Network"
                          value={resourceHistory.network[resourceHistory.network.length - 1]?.toFixed(1) || "0"}
                          unit="%"
                          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
</svg>}
                          isUpdating={updatingFields.has('network') || isUpdating}
                          color="green"
                        />
                      </div>
                    </div>
                  )}

                  {section.id === 'latency' && (
                    <div className="card border border-gray-200 rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="font-mono text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider border-b border-gray-300 pb-2">Backend Latency Details</h3>
                      <div className="space-y-2">
                        {metrics?.backendLatency &&
                          Object.entries(metrics.backendLatency).map(
                            ([backend, latency]) => (
                              <div
                                key={backend}
                                className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                              >
                                <span className="text-gray-700 capitalize">{backend}</span>
                                <span
                                  className={`font-mono ${
                                    latency < 100
                                      ? "text-green-600"
                                      : latency < 200
                                      ? "text-yellow-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {latency}ms
                                </span>
                              </div>
                            ),
                          )}
                      </div>
                    </div>
                  )}

                  {section.id === 'processes' && (
                    <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="font-semibold text-gray-900 mb-4">AI Processes</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">Process</th>
                              <th className="text-left p-2">Status</th>
                              <th className="text-left p-2">Progress</th>
                              <th className="text-left p-2">Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {processes.map(process => (
                              <tr key={process.id} className="border-b">
                                <td className="p-2">{process.name}</td>
                                <td className="p-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    process.status === 'running' ? 'bg-green-100 text-green-700' :
                                    process.status === 'completed' ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {process.status}
                                  </span>
                                </td>
                                <td className="p-2">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-red-600 h-2 rounded-full"
                                      style={{ width: `${Math.min(process.progress, 100)}%` }}
                                    />
                                  </div>
                                </td>
                                <td className="p-2">{process.duration}s</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {section.id === 'charts' && (
                    <Suspense fallback={<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="animate-pulse h-48 bg-gray-200 rounded"></div>
                      </div>
                      <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="animate-pulse h-48 bg-gray-200 rounded"></div>
                      </div>
                    </div>}>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                          <h3 className="font-semibold text-gray-900 mb-4">Revenue Trend</h3>
                          <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={REVENUE_DATA}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>

                        <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                          <h3 className="font-semibold text-gray-900 mb-4">Transaction Types</h3>
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: "PromptPay", value: 65 },
                                  { name: "Credit Card", value: 25 },
                                  { name: "Bank Transfer", value: 10 }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                <Cell fill="#3b82f6" />
                                <Cell fill="#10b981" />
                                <Cell fill="#f59e0b" />
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </Suspense>
                  )}

                  {section.id === 'quick-actions' && (
                    <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="font-semibold text-gray-900 mb-4">Admin Quick Actions</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <button className="p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-left">
                          <div className="text-red-600 font-semibold mb-1">Manage Users</div>
                          <div className="text-xs text-gray-500">Add, edit, or remove users</div>
                        </button>
                        <button className="p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors text-left">
                          <div className="text-green-600 font-semibold mb-1">System Config</div>
                          <div className="text-xs text-gray-500">Configure system settings</div>
                        </button>
                        <button className="p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-left">
                          <div className="text-red-600 font-semibold mb-1">View Logs</div>
                          <div className="text-xs text-gray-500">Access system logs</div>
                        </button>
                        <button className="p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-left">
                          <div className="text-red-600 font-semibold mb-1">Emergency Stop</div>
                          <div className="text-xs text-gray-500">Stop all services</div>
                        </button>
                      </div>
                    </div>
                  )}

                  {section.id === 'system-health' && (
                    <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="font-semibold text-gray-900 mb-4">System Health</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-medium text-gray-700">API Gateway</span>
                          </div>
                          <span className="text-green-600 font-semibold">Healthy</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-medium text-gray-700">Database</span>
                          </div>
                          <span className="text-green-600 font-semibold">Healthy</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <span className="font-medium text-gray-700">Redis Cache</span>
                          </div>
                          <span className="text-yellow-600 font-semibold">Warning</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-medium text-gray-700">Worker Nodes</span>
                          </div>
                          <span className="text-green-600 font-semibold">5/5 Active</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {section.id === 'security-alerts' && (
                    <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="font-semibold text-gray-900 mb-4">Security Alerts</h3>
                      <div className="space-y-3">
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-red-700">Failed Login Attempts</span>
                            <span className="text-xs text-red-600">2 min ago</span>
                          </div>
                          <p className="text-sm text-gray-600">15 failed attempts from IP 192.168.1.100</p>
                        </div>
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-yellow-700">Unusual API Usage</span>
                            <span className="text-xs text-yellow-600">15 min ago</span>
                          </div>
                          <p className="text-sm text-gray-600">High rate of requests from user ID 4521</p>
                        </div>
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-red-700">System Update Available</span>
                            <span className="text-xs text-red-600">1 hour ago</span>
                          </div>
                          <p className="text-sm text-gray-600">Security patch v2.1.3 ready for deployment</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {section.id === 'deployment-status' && (
                    <div className="card border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <h3 className="font-semibold text-gray-900 mb-4">Deployment Status</h3>
                      <div className="space-y-4">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-green-700">Production</span>
                            <span className="px-2 py-1 bg-green-600 text-white text-xs font-medium rounded">Live</span>
                          </div>
                          <div className="text-sm text-gray-600">Version 2.0.0 • Deployed 2 days ago</div>
                        </div>
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-red-700">Staging</span>
                            <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">Live</span>
                          </div>
                          <div className="text-sm text-gray-600">Version 2.1.0-beta • Deployed 4 hours ago</div>
                        </div>
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-gray-700">Development</span>
                            <span className="px-2 py-1 bg-gray-600 text-white text-xs font-medium rounded">Building</span>
                          </div>
                          <div className="text-sm text-gray-600">Version 2.1.1-dev • Last commit 10 min ago</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </main>
      </div>
    </div>
  );
}
